import log from '@/lib/logger';
import {useState} from 'react'
import {usePricingTranslation, useCommonTranslation} from '@/i18n/hooks'
import {useFieldArray, useForm} from 'react-hook-form'
import {zodResolver} from '@hookform/resolvers/zod'
import {Info, Plus, Trash} from 'lucide-react'
import {Button} from '@/components/tailwind/button'
import {Input} from '@/components/tailwind/input'
import {Textarea} from '@/components/tailwind/textarea'
import {Description, Field, FieldGroup, Label} from '@/components/tailwind/fieldset'
import ValidationAlert from '@/components/validation-alert'
import {Divider} from '@/components/tailwind/divider'
import {Badge} from '@/components/tailwind/badge'
import {
    CreatePricingSchemeFormData,
    createPricingSchemeSchema,
    UpdatePricingSchemeFormData,
    updatePricingSchemeSchema
} from './pricing-validation'
import {PricingScheme} from '@/models/pricing'
import {createPricingScheme, updatePricingScheme, updatePricingSchemeMetadata} from '@/services/pricing-services'
import {useAuth} from "@/context/use-auth"
import {useBillingUnitLabel} from "@/hooks/use-billing-unit-label"

interface PricingSchemeFormProps {
    organizationId?: string // Not used anymore since API determines organization from auth
    initialData?: PricingScheme | null
    onSuccess: () => void
    onCancel: () => void
}

export function PricingSchemeForm({
                                      initialData,
                                      onSuccess,
                                      onCancel
                                  }: PricingSchemeFormProps) {
    const {t} = usePricingTranslation()
    const {t: tCommon} = useCommonTranslation()
    const {token} = useAuth()
    const {unitLabel, unitLabelLong} = useBillingUnitLabel()
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const isEditing = !!initialData

    // Check if the scheme is currently active (meaning its validFrom is in the past or today)
    const isActiveScheme = isEditing && initialData && new Date(initialData.validFrom) <= new Date()

    // Bestimme das Schema basierend darauf, ob wir bearbeiten oder erstellen
    const schema = isEditing ? updatePricingSchemeSchema : createPricingSchemeSchema

    // Helper function to get next month start
    function getNextMonthStart(): string {
        const now = new Date()
        const year = now.getMonth() === 11 ? now.getFullYear() + 1 : now.getFullYear()
        const month = now.getMonth() === 11 ? 0 : now.getMonth() + 1
        const monthStr = (month + 1).toString().padStart(2, '0')
        return `${year}-${monthStr}-01`
    }

    const defaultFormData = {
        name: '',
        description: '',
        modelType: 'HOURLY_TIERED' as const,
        validFrom: getNextMonthStart(),
        tiers: [
            {
                tierOrder: 1,
                fromUnit: 1,
                toUnit: 1,
                pricePerUnit: 2.50,
                unitType: 'HOUR' as const
            },
            {
                tierOrder: 2,
                fromUnit: 2,
                toUnit: null,
                pricePerUnit: 1.50,
                unitType: 'HOUR' as const
            }
        ]
    }

    const form = useForm<CreatePricingSchemeFormData | UpdatePricingSchemeFormData>({
        resolver: zodResolver(schema),
        defaultValues: initialData ? {
            id: initialData.id,
            name: initialData.name,
            description: initialData.description || '',
            modelType: initialData.modelType,
            validFrom: initialData.validFrom.split('T')[0], // Convert to YYYY-MM-DD
            tiers: initialData.tiers.map(tier => ({
                tierOrder: tier.tierOrder,
                fromUnit: tier.fromUnit,
                toUnit: tier.toUnit,
                pricePerUnit: tier.pricePerUnit,
                unitType: tier.unitType
            }))
        } : defaultFormData
    })

    const {fields, append, remove} = useFieldArray({
        control: form.control,
        name: 'tiers'
    })

    const {handleSubmit, formState: {errors}, watch, setValue} = form

    // Debug errors in development
    if (process.env.NODE_ENV === 'development' && errors.tiers) {
        log.warn('All form errors:', errors)
        log.warn('Tier errors specifically:', errors.tiers)
    }

    async function onSubmit(data: CreatePricingSchemeFormData | UpdatePricingSchemeFormData) {
        try {
            setSubmitting(true)
            setError(null)

            // Transform data to match API expectations
            const transformedData = {
                ...data,
                // Convert date to ISO-8601 instant format (start of day in UTC)
                validFrom: new Date(data.validFrom + 'T00:00:00.000Z').toISOString(),
                tiers: data.tiers.map(tier => ({
                    tierOrder: tier.tierOrder,
                    fromUnit: tier.fromUnit,
                    toUnit: tier.toUnit === undefined ? null : tier.toUnit,
                    pricePerUnit: tier.pricePerUnit,
                    unitType: tier.unitType
                }))
            }

            if (isEditing) {
                if (isActiveScheme) {
                    // For active schemes, only update metadata (name and description)
                    await updatePricingSchemeMetadata(token, initialData!.id, {
                        name: data.name,
                        description: data.description
                    })
                } else {
                    // For future schemes, allow full updates
                    await updatePricingScheme(token, transformedData as any)
                }
            } else {
                await createPricingScheme(token, transformedData as any)
            }

            onSuccess()
        } catch (err: any) {
            // Extract error message from API response
            let errorMessage = isEditing
                ? t('messages.updateError')
                : t('messages.createError')

            // Handle ProblemDetail response from backend
            if (err.detail) {
                // The backend sends i18n keys as detail
                // Check if it's an i18n key or a direct message
                if (err.detail.includes('.')) {
                    // It's an i18n key - try to translate it
                    // Remove namespace prefix if present (e.g., "pricing.scheme.name.required" -> "scheme.name.required")
                    const keyParts = err.detail.split('.')
                    if (keyParts[0] === 'pricing' && keyParts.length > 1) {
                        // Try with namespace removed
                        const shortKey = keyParts.slice(1).join('.')
                        errorMessage = t(shortKey as any) || err.detail
                    } else {
                        // Try direct translation
                        errorMessage = t(err.detail as any) || err.detail
                    }
                } else {
                    errorMessage = err.detail
                }
            } else if (err.title) {
                errorMessage = err.title
            }

            setError(errorMessage)
            log.error('Error saving pricing scheme:', err)
        } finally {
            setSubmitting(false)
        }
    }

    function addTier() {
        const existingTiers = watch('tiers')
        const nextOrder = Math.max(...existingTiers.map(t => t.tierOrder), 0) + 1
        const lastTier = existingTiers[existingTiers.length - 1]

        append({
            tierOrder: nextOrder,
            fromUnit: lastTier ? (lastTier.toUnit || lastTier.fromUnit) + 1 : 1,
            toUnit: null,
            pricePerUnit: 1.00,
            unitType: 'HOUR' as const
        })
    }

    function removeTier(index: number) {
        remove(index)
        // After removal, the array indices are automatically adjusted
        // We need to update tierOrder for all remaining tiers
        setTimeout(() => {
            const currentTiers = watch('tiers')
            currentTiers.forEach((_, i) => {
                setValue(`tiers.${i}.tierOrder`, i + 1)
            })
        }, 0)
    }

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {error && (
                <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg p-4">
                    <p className="text-red-700 dark:text-red-300 text-sm">{error}</p>
                </div>
            )}

            {isActiveScheme && (
                <div
                    className="bg-blue-50 dark:bg-blue-950 border-l-4 border-blue-400 dark:border-blue-500 p-4"
                    data-testid="active-scheme-restriction-banner"
                >
                    <div className="flex items-start gap-3">
                        <Info className="h-5 w-5 text-blue-400 dark:text-blue-500 mt-0.5 flex-shrink-0"/>
                        <div>
                            <p className="text-blue-700 dark:text-blue-300 text-sm font-medium">
                                {t('messages.activeSchemeRestriction')}
                            </p>
                            <p className="text-blue-600 dark:text-blue-400 text-sm mt-1">
                                {t('messages.activeSchemeRestrictionDescription')}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Basic Information */}
            <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                    {t('form.basicInformation')}
                </h3>

                <FieldGroup>
                    <Field>
                        <Label>{t('form.name')}</Label>
                        <Input
                            {...form.register('name')}
                            placeholder={t('form.namePlaceholder')}
                            invalid={!!errors.name}
                            data-testid="price-config-title"
                        />
                        {errors.name && <ValidationAlert message={errors.name.message ? t(errors.name.message) : ''}/>}
                    </Field>

                    <Field>
                        <Label>{t('form.description')}</Label>
                        <Textarea
                            {...form.register('description')}
                            placeholder={t('form.descriptionPlaceholder')}
                            rows={3}
                            invalid={!!errors.description}
                            data-testid="price-config-description"
                        />
                        {errors.description && <ValidationAlert message={t(errors.description.message || '')}/>}
                    </Field>

                </FieldGroup>
            </div>

            <Divider/>

            {/* Validity Period */}
            <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{t('form.validityPeriod')}</h3>

                <FieldGroup>
                    <Field>
                        <Label>{t('form.validFrom')}</Label>
                        <Input
                            type="date"
                            {...form.register('validFrom')}
                            invalid={!!errors.validFrom}
                            disabled={isActiveScheme}
                        />
                        <Description>{t('form.validFromHelp')}</Description>
                        {errors.validFrom && <ValidationAlert message={t(errors.validFrom.message || '')}/>}
                    </Field>
                </FieldGroup>
            </div>

            {!isActiveScheme && <Divider/>}

            {/* Pricing Tiers */}
            <div>
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                        {t('form.pricingTiers')}
                    </h3>
                    {!isActiveScheme && (
                        <Button 
                            type="button" 
                            plain
                            onClick={addTier}
                            className="flex items-center"
                        >
                            <Plus className="h-4 w-4 mr-1"/>
                            {t('form.addTier')}
                        </Button>
                    )}
                </div>

                {!isActiveScheme && (
                    /* Example hint */
                    <div
                        className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
                        <div className="flex items-start gap-2">
                            <Info className="h-5 w-5 text-blue-500 dark:text-blue-400 mt-0.5 flex-shrink-0"/>
                            <p className="text-blue-700 dark:text-blue-300 text-sm">
                                {t('examples.defaultTierStructure', {unit: unitLabelLong})}
                            </p>
                        </div>
                    </div>
                )}

                <div className="space-y-4">
                    {isActiveScheme && initialData ? (
                        // Read-only display for active schemes
                        initialData.tiers.map((tier) => (
                            <div key={tier.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                                <div className="flex items-center justify-between mb-4">
                                    <Badge color="zinc">
                                        {t('form.tierOrder')} {tier.tierOrder}
                                    </Badge>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <Field>
                                        <Label>{t('form.fromUnit', {unit: unitLabelLong})}</Label>
                                        <div className="relative">
                                            <Input
                                                type="number"
                                                value={tier.fromUnit}
                                                disabled
                                                readOnly
                                            />
                                            <div
                                                className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                                <span
                                                    className="text-gray-500 dark:text-gray-400 text-sm">{t('units.hours', {unit: unitLabel})}</span>
                                            </div>
                                        </div>
                                    </Field>

                                    <Field>
                                        <Label>{t('form.toUnit', {unit: unitLabelLong})}</Label>
                                        <div className="relative">
                                            <Input
                                                type="number"
                                                placeholder={t('form.unlimitedUnits')}
                                                value={tier.toUnit || ''}
                                                disabled
                                                readOnly
                                            />
                                            <div
                                                className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                                <span
                                                    className="text-gray-500 dark:text-gray-400 text-sm">{t('units.hours', {unit: unitLabel})}</span>
                                            </div>
                                        </div>
                                    </Field>

                                    <Field>
                                        <Label>{t('form.pricePerUnit', {unit: unitLabelLong})}</Label>
                                        <div className="relative">
                                            <Input
                                                type="number"
                                                step="0.01"
                                                value={tier.pricePerUnit}
                                                disabled
                                                readOnly
                                            />
                                            <div
                                                className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                                <span
                                                    className="text-gray-500 dark:text-gray-400 text-sm">{t('currency.euro')}</span>
                                            </div>
                                        </div>
                                    </Field>
                                </div>
                            </div>
                        ))
                    ) : (
                        // Editable fields for new/future schemes
                        fields.map((field, index) => (
                            <div key={field.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                                <div className="flex items-center justify-between mb-4">
                                    <Badge color="zinc">
                                        {t('form.tierOrder')} {index + 1}
                                    </Badge>
                                    {fields.length > 1 && (
                                        <Button
                                            type="button"
                                            plain
                                            onClick={() => removeTier(index)}
                                            className="flex items-center text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                                        >
                                            <Trash className="h-4 w-4 mr-1"/>
                                            {t('form.removeTier')}
                                        </Button>
                                    )}
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <Field>
                                        <Label>{t('form.fromUnit', {unit: unitLabelLong})}</Label>
                                        <div className="relative">
                                            <Input
                                                type="number"
                                                min={1}
                                                {...form.register(`tiers.${index}.fromUnit`, {
                                                    valueAsNumber: true,
                                                    validate: (value) => !isNaN(value) && value > 0
                                                })}
                                                invalid={!!errors.tiers?.[index]?.fromUnit}
                                            />
                                            <div
                                                className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                                <span
                                                    className="text-gray-500 dark:text-gray-400 text-sm">{t('units.hours', {unit: unitLabel})}</span>
                                            </div>
                                        </div>
                                        {errors.tiers?.[index]?.fromUnit && (
                                            <ValidationAlert message={t(errors.tiers[index].fromUnit.message || '')}/>
                                        )}
                                    </Field>

                                    <Field>
                                        <Label>{t('form.toUnit', {unit: unitLabelLong})}</Label>
                                        <div className="space-y-2">
                                            <div className="relative">
                                                <Input
                                                    type="number"
                                                    min={1}
                                                    placeholder={t('form.unlimitedUnits')}
                                                    {...form.register(`tiers.${index}.toUnit`, {
                                                        setValueAs: (value) => {
                                                            if (value === '' || value === null || value === undefined) {
                                                                return null
                                                            }
                                                            const num = Number(value)
                                                            return isNaN(num) ? null : num
                                                        }
                                                    })}
                                                    invalid={!!errors.tiers?.[index]?.toUnit}
                                                />
                                                <div
                                                    className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                                    <span
                                                        className="text-gray-500 dark:text-gray-400 text-sm">{t('units.hours', {unit: unitLabel})}</span>
                                                </div>
                                            </div>
                                            {errors.tiers?.[index]?.toUnit && (
                                                <ValidationAlert message={t(errors.tiers[index].toUnit.message || '')}/>
                                            )}
                                        </div>
                                    </Field>

                                    <Field>
                                        <Label>{t('form.pricePerUnit', {unit: unitLabelLong})}</Label>
                                        <div className="relative">
                                            <Input
                                                type="number"
                                                step="0.01"
                                                min={0}
                                                {...form.register(`tiers.${index}.pricePerUnit`, {
                                                    valueAsNumber: true,
                                                    validate: (value) => !isNaN(value) && value >= 0
                                                })}
                                                invalid={!!errors.tiers?.[index]?.pricePerUnit}
                                            />
                                            <div
                                                className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                                <span
                                                    className="text-gray-500 dark:text-gray-400 text-sm">{t('currency.euro')}</span>
                                            </div>
                                        </div>
                                        {errors.tiers?.[index]?.pricePerUnit && (
                                            <ValidationAlert
                                                message={t(errors.tiers[index].pricePerUnit.message || '')}/>
                                        )}
                                    </Field>
                                </div>
                            </div>
                        ))
                    )}
                </div>

                {/* Tier validation errors - only for non-active schemes */}
                {!isActiveScheme && errors.tiers && (
                    <div className="space-y-2">
                        <ValidationAlert
                            message={(() => {
                                const tierError = errors.tiers;

                                // Try different ways to access the message
                                if (typeof tierError === 'object' && tierError !== null) {
                                    // Direct message property
                                    if ('message' in tierError && typeof tierError.message === 'string') {
                                        return t(tierError.message);
                                    }

                                    // Root.message structure
                                    if ('root' in tierError && tierError.root && typeof tierError.root === 'object' && 'message' in tierError.root) {
                                        return t(tierError.root.message as string);
                                    }

                                    // Array-like errors with message
                                    if (Array.isArray(tierError) && tierError.length > 0 && tierError[0] && 'message' in tierError[0]) {
                                        return t(tierError[0].message);
                                    }
                                }

                                // If we can't find a specific message, check if it's the overlap error
                                // by looking at the validation state
                                const tiersValue = watch('tiers');
                                if (tiersValue && tiersValue.length > 1) {
                                    const sortedTiers = [...tiersValue].sort((a, b) => a.tierOrder - b.tierOrder);
                                    for (let i = 1; i < sortedTiers.length; i++) {
                                        const prevTier = sortedTiers[i - 1];
                                        const currentTier = sortedTiers[i];
                                        if (prevTier.toUnit !== null && prevTier.toUnit !== undefined && prevTier.toUnit >= currentTier.fromUnit) {
                                            return t('validation.tiers.noOverlap');
                                        }
                                    }
                                }

                                return t('validation.tiers.generalError');
                            })()}
                        />
                    </div>
                )}

            </div>

            {/* Form Actions */}
            <div className="flex items-center justify-end gap-3 pt-6 border-t border-gray-200 dark:border-gray-700">
                <Button 
                    type="button" 
                    plain
                    onClick={onCancel}
                >
                    {tCommon('buttons.cancel.text')}
                </Button>
                <Button 
                    type="submit" 
                    disabled={submitting}
                    data-testid="submit-price-config"
                >
                    {submitting ? t('form.saving') : tCommon('buttons.submit.text')}
                </Button>
            </div>
        </form>
    )
}

